import {
  MoreActivityStatsModal,
  type MoreActivityStatsModalProps,
} from '@components/activity-logs/modal/MoreActivityStatsModal';
import { ArticleReadModal } from '@components/article-architect/portal-preview/ArticleReadModal';
import { HotTopicAllModal } from '@components/dashboard/hot-topics/HotTopicAllModal';
import { HotTopicRelatedQuestionsModal } from '@components/dashboard/hot-topics/HotTopicRelatedQuestionsModal';
import {
  CreateSubfolderModal,
  type CreateSubfolderModalProps,
} from '@components/data-sources/data-sources-folders/CreateSubfolderModal';
import EditDataSource from '@components/data-sources/EditDataSource';
import InfoDataSource from '@components/data-sources/InfoDataSource';
import MoveDataSource from '@components/data-sources/MoveDataSource';
import ShareDataSource from '@components/data-sources/ShareDataSource';
import { UploadDocumentsProgressModal } from '@components/data-sources/UploadDocumentsProgressModal';
import { WebScraperProgressModal } from '@components/data-sources/WebScraperProgressModal';
import { GateSelectSubflowModal } from '@components/designer/modal/GateSelectSubflowModal';
import { HandoverSelectInboxModal } from '@components/designer/modal/HandoverSelectInboxModal';
import { IntentCheckInitModal } from '@components/designer/modal/IntentCheckInitModal';
import { SwitchRevisionModal } from '@components/designer/modal/SwitchRevisionModal';
import { EditSubFlowForm } from '@components/designer/modal/subflow/EditSubFlowForm';
import {
  IntelligenceTopicModal,
  type TIntelligenceTopicModalProps,
} from '@components/home/v2/intelligence/IntelligenceTopicModal';
import { RevisionSetDefaultModal } from '@components/revisions/modal/RevisionSetDefaultModal';
import Enable2FaModal from '@components/settings-page/modal/2fa/Enable2FAModal';
import CreateKeywordModal from '@components/settings-page/modal/conversation-keywords/CreateKeywordModal';
import EditKeywordModal from '@components/settings-page/modal/conversation-keywords/EditKeywordModal';
import AddMemberModal from '@components/settings-page/modal/member-settings/AddMemberModal';
import BulkInviteDataModal, {
  type BulkInviteDataModalProps,
} from '@components/settings-page/modal/member-settings/BulkInviteDataModal';
import {
  BulkInviteReviewModal,
  type TBulkInviteReviewModalProps,
} from '@components/settings-page/modal/member-settings/BulkInviteReviewModal';
import ShareMemberModal from '@components/settings-page/modal/member-settings/ShareMemberModal';
import InfoSubflowTemplate from '@components/settings-page/modal/subflow-templates/InfoSubflowTemplate';
import SaveSubflowTemplate from '@components/settings-page/modal/subflow-templates/SaveSubflowTemplate';
import CreateTemplateResponseModal from '@components/settings-page/modal/template-response/CreateTemplateResponseModal';
import EditTemplateResponseModal from '@components/settings-page/modal/template-response/EditTemplateResponseModal';
import SuggestionBoxModal from '@components/suggestion-box/modals/SuggestionBoxModal';
import { ExperimentResultModal } from '@components/test-accuracy/ExperimentResultModal';
import { TestResultsReviewDataModal } from '@components/test-accuracy/TestResultsReviewDataModal';
import ConversationInfoModal from '@components/train-ai/modals/ConversationInfoModal';
import ConversationKeywordsModal from '@components/train-ai/modals/ConversationKeywordsModal';
import { ConversationReviewModal } from '@components/train-ai/modals/conversation-review/ConversationReviewModal';
import type { TConversationReviewModalProps } from '@components/train-ai/modals/conversation-review/conversation-review-modal.type';
import DataSourceGenerationModal from '@components/train-ai/modals/data-source-generation/DataSourceGenerationModal';
import FactCheckingModal from '@components/train-ai/modals/FactCheckingModal';
import FactCheckingPrestartModal from '@components/train-ai/modals/FactCheckingPrestartModal';
import type { TFactCheckingModalProps } from '@components/train-ai/modals/fact-checking-modal.type';
import InvalidDataSourceContentModal from '@components/train-ai/modals/invalid-data-source-content/InvalidDataSourceContentModal';
import ManualTrainingInfoModal from '@components/train-ai/modals/ManualTrainingInfoModal';
import PostProcessConfigurationModal, {
  type Props as PostProcessConfigurationModalProps,
} from '@components/train-ai/modals/PostProcessConfigurationModal';
import WeeklyStatisticsModal from '@components/train-ai/modals/WeeklyStatisticsModal';
import type { TTranslationKeys } from '@config/i18n';
import type { KnowledgeArticleDTO } from '@conversed/rest/src/contracts/article-architect/article-architect.dto';
import type {
  InvalidDataSourceContent,
  LLMBlockSelectionDTO,
} from '@conversed/rest/src/contracts/data-sources/data-sources.dto';
import type { TCheckResult } from '@conversed/rest/src/contracts/llm-usages/check-results';
import type {
  TConversationDetailsSchema,
  TWeeklyStatisticsResponse,
} from '@conversed/rest/src/contracts/llm-usages/llm-usages.contract';
import type { HotTopicWithRelatedDTO } from '@conversed/rest/src/contracts/platform-settings/platform-settings.dto';
import type { CompanyMemberDTO } from '@conversed/shared/src/central/dto/company';
import type { UploadDocumentsProgressModalProps } from '@conversed/shared/src/central/dto/upload-documents.dto';
import type { WebScraperProgressModalProps } from '@conversed/shared/src/central/dto/web-scraper.dto';
import type { Gate } from '@conversed/shared/src/central/types/blocks/Gate';
import type { Handover } from '@conversed/shared/src/central/types/blocks/Handover';
import type { IntentCheckBlock } from '@conversed/shared/src/central/types/blocks/IntentCheckBlock';
import type { TExperimentResult } from '@conversed/shared/src/central/types/experiment-result';
import type { TTestAccuracyResult } from '@conversed/shared/src/central/types/test-accuracy';
import type { DataSourceGenerationModalProps } from '@interfaces/TDataSourceGeneration';
import type { ReactElement } from 'react';
import { EditRefParamDestinationModal } from '../../designer/modal/blocks/router/advanced/EditRefParamDestinationModal';
import { EditRulesForDestinationModal } from '../../designer/modal/blocks/router/advanced/EditRulesForDestinationModal';
import { BlockTitleEditForm } from '../../designer/modal/form/BlockTitleEditForm';
import { CreateSubFlowForm } from '../../designer/modal/subflow/CreateSubFlowForm';
import { GDPREmailPrompt } from '../../gdpr/modal/GDPREmailPrompt';
import { RevisionAddModal } from '../../revisions/modal/RevisionAddModal';
import { RevisionEditModal } from '../../revisions/modal/RevisionEditModal';
import Disable2FaModal from '../../settings-page/modal/2fa/Disable2FAModal';
import CreateAgentModal from '../../settings-page/modal/agents/CreateAgentModal';
import EditAgentModal from '../../settings-page/modal/agents/EditAgentModal';
import CreateCannedResponseModal from '../../settings-page/modal/canned-responses/CreateCannedResponseModal';
import EditCannedResponseModal from '../../settings-page/modal/canned-responses/EditCannedResponseModal';
import {
  CompanyLifecycleModal,
  type CompanyLifecycleModalProps,
} from '../../settings-page/modal/company/CompanyLifecycleModal';
import CreateExportConfigModal from '../../settings-page/modal/export-settings/CreateExportConfigModal';
import EditExportConfigModal from '../../settings-page/modal/export-settings/EditExportConfigModal';
import FacebookSettingsModal from '../../settings-page/modal/facebook/FacebookSettingsModal';
import CreateInboxModal from '../../settings-page/modal/inboxes/CreateInboxModal';
import EditInboxModal from '../../settings-page/modal/inboxes/EditInboxModal';
import { CreateLabelModal } from '../../settings-page/modal/labels/CreateLabelModal';
import { EditLabelModal } from '../../settings-page/modal/labels/EditLabelModal';
import CreateLinkModal from '../../settings-page/modal/links/CreateLinkModal';
import EditLinkModal from '../../settings-page/modal/links/EditLinkModal';
import LinkStatisticsModal from '../../settings-page/modal/links/LinkStatisticsModal';
import { ChangePasswordModal } from '../../settings-page/modal/password/ChangePasswordModal';
import { ManageTimezoneModal } from '../../settings-page/modal/timezone/ManageTimezoneModal';
import { ManageUserDataMappingModal } from '../../settings-page/modal/user-data-mapping/ManageUserDataMappingModal';
import { CopyWebclientConfigModal } from '../../settings-page/modal/webclient/CopyWebclientConfigModal';
import CreateWebclientConfigModal from '../../settings-page/modal/webclient/CreateWebclientConfigModal';
import DeleteWebclientConfigModal from '../../settings-page/modal/webclient/DeleteWebclientConfigModal';
import EditWebclientConfigModal from '../../settings-page/modal/webclient/EditWebclientConfigModal';
import SetupWebclient from '../../settings-page/modal/webclient/SetupWebclient';
import WebclientSnippetModal from '../../settings-page/modal/webclient/WebclientSnippetModal';
import AddWebclientRestartPolicyWhitelistModal from '../../settings-page/modal/webclient/whitelist/AddWebclientRestartPolicyWhitelistModal';
import AddWebclientWhitelistModal from '../../settings-page/modal/webclient/whitelist/AddWebclientWhitelistModal';
import EditDateModal from '../../settings-page/schedule-calendar/components/modals/EditDateModal';
import EditDayModal from '../../settings-page/schedule-calendar/components/modals/EditDayModal';
import GeneralInfoModal from './GeneralInfoModal';
import GeneralPromptModal from './GeneralPromptModal';
import GeneralPromptModalV2 from './GeneralPromptModalV2';

type ModalProps = GeneralPromptModalProps | GeneralInfoModalProps | any;

type Translator = (key: TTranslationKeys) => string;

type ModalSize =
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | 'full'
  | 'w-90';

// TODO: Unify modal types
type TModal = {
  getComponent: (props: ModalProps) => ReactElement;
  getTitle?: (t: Translator) => string;
  getTitleIcon?: {
    icon: string;
    color: string;
  };
  modalSize?: ModalSize;
  modalVariant?: 'v2';
  headerMargin?: string;
  titleSize?: string;
  titleColor?: string;
  modalBgClass?: string;
  roundedClass?: string;
};

const modalMap = {
  'add-agent': {
    getComponent: () => <CreateAgentModal />,
    getTitle: (t: Translator) => t('modals.title.add-agent'),
  },
  'edit-agent': {
    getComponent: () => <EditAgentModal />,
    getTitle: (t: Translator) => t('modals.title.edit-agent'),
  },
  'add-inbox': {
    getComponent: () => <CreateInboxModal />,
    getTitle: (t: Translator) => t('modals.title.add-inbox'),
  },
  'edit-inbox': {
    getComponent: () => <EditInboxModal />,
    getTitle: (t: Translator) => t('modals.title.edit-inbox'),
  },
  'add-canned-response': {
    getComponent: () => <CreateCannedResponseModal />,
    getTitle: (t: Translator) => t('modals.title.add-canned-response'),
  },
  'edit-canned-response': {
    getComponent: () => <EditCannedResponseModal />,
    getTitle: (t: Translator) => t('modals.title.edit-canned-response'),
  },
  'edit-webclient-config': {
    getComponent: () => <EditWebclientConfigModal />,
    getTitle: (t: Translator) => t('modals.title.edit-webclient-config'),
  },
  'webclient-snippet': {
    getComponent: () => <WebclientSnippetModal />,
    getTitle: (t: Translator) => t('modals.title.snippet-webclient-connector'),
  },
  'create-webclient-config': {
    getComponent: () => <CreateWebclientConfigModal />,
    getTitle: (t: Translator) => t('modals.title.create-webclient-config'),
  },
  'copy-webclient-config': {
    getComponent: () => <CopyWebclientConfigModal />,
    getTitle: (t: Translator) => t('modals.title.copy-webclient-config'),
  },
  'delete-webclient-config': {
    getComponent: () => <DeleteWebclientConfigModal />,
    getTitle: (t: Translator) => t('modals.title.delete-webclient-config'),
  },
  'add-webclient-whitelist': {
    getComponent: () => <AddWebclientWhitelistModal />,
    getTitle: (t: Translator) => t('modals.title.add-webclient-whitelist'),
  },
  'add-webclient-restart-policy-whitelist': {
    getComponent: () => <AddWebclientRestartPolicyWhitelistModal />,
    getTitle: (t: Translator) =>
      t('modals.title.add-webclient-ref-param-whitelist'),
  },
  'add-label': {
    getComponent: () => <CreateLabelModal />,
    getTitle: (t: Translator) => t('modals.title.add-label'),
  },
  'edit-label': {
    getComponent: () => <EditLabelModal />,
    getTitle: (t: Translator) => t('modals.title.edit-label'),
  },
  'setup-webclient': {
    getComponent: () => <SetupWebclient />,
    getTitle: (t: Translator) => t('modals.title.setup-webclient'),
  },
  'facebook-settings': {
    getComponent: () => <FacebookSettingsModal />,
    getTitle: (t: Translator) => t('modals.title.settings-facebook'),
  },
  'add-link': {
    getComponent: () => <CreateLinkModal />,
    getTitle: (t: Translator) => t('modals.title.create-link'),
  },
  'edit-link': {
    getComponent: () => <EditLinkModal />,
    getTitle: (t: Translator) => t('modals.title.edit-link'),
  },
  'link-statistics': {
    getComponent: () => <LinkStatisticsModal />,
    getTitle: (t: Translator) => t('modals.title.link-statistics'),
  },
  'enable-2fa': {
    getComponent: () => <Enable2FaModal />,
    getTitle: (t: Translator) => t('modals.title.enable-2fa'),
  },
  'disable-2fa': {
    getComponent: () => <Disable2FaModal />,
    getTitle: (t: Translator) => t('modals.title.disable-2fa'),
  },
  'change-password': {
    getComponent: () => <ChangePasswordModal />,
    getTitle: (t: Translator) => t('modals.title.change-password'),
  },
  'general-prompt-modal': {
    getComponent: (props: GeneralPromptModalProps) => (
      <GeneralPromptModal {...props} />
    ),
  },
  'general-prompt-modal-v2': {
    getComponent: (props: GeneralPromptModalProps) => (
      <GeneralPromptModalV2 {...props} />
    ),
    modalVariant: 'v2' as const,
    modalSize: 'sm' as ModalSize,
  },
  'general-info-modal': {
    getComponent: (props: GeneralInfoModalProps) => (
      <GeneralInfoModal {...props} />
    ),
  },
  'create-export-config': {
    getComponent: () => <CreateExportConfigModal />,
    getTitle: (t: Translator) => t('modals.title.create-export-config'),
  },
  'edit-export-config': {
    getComponent: () => <EditExportConfigModal />,
    getTitle: (t: Translator) => t('modals.title.edit-export-config'),
  },
  'send-gdpr-modal': {
    getComponent: () => <GDPREmailPrompt />,
    getTitle: (t: Translator) => t('modals.title.send-gdpr'),
  },
  'edit-router-ref-param': {
    getComponent: () => <EditRefParamDestinationModal />,
    getTitle: (t: Translator) => t('modals.title.set-ref-param'),
  },
  'edit-rules-param': {
    getComponent: () => <EditRulesForDestinationModal />,
    getTitle: (t: Translator) => t('modals.title.set-rules-param'),
  },
  'edit-date-modal': {
    getComponent: () => <EditDateModal />,
    getTitle: (t: Translator) => t('modals.title.edit-date-modal'),
  },
  'edit-day-modal': {
    getComponent: () => <EditDayModal />,
    getTitle: (t: Translator) => t('modals.title.edit-day-modal'),
  },
  'manage-user-data-mapping': {
    getComponent: () => <ManageUserDataMappingModal />,
    getTitle: (t: Translator) => t('modals.title.manage-user-data-mapping'),
  },
  'manage-timezone': {
    getComponent: () => <ManageTimezoneModal />,
    getTitle: (t: Translator) => t('modals.title.timezone'),
  },
  'add-subflow': {
    getComponent: () => <CreateSubFlowForm />,
    getTitle: (t: Translator) => t('modals.title.add-subflow'),
  },
  'edit-subflow': {
    getComponent: (props: {
      currentSubFlowName: string;
      currentRevisionId: string;
    }) => <EditSubFlowForm {...props} />,
    getTitle: (t: Translator) => t('modals.title.edit-subflow'),
  },
  'edit-block-title': {
    getComponent: () => <BlockTitleEditForm />,
    getTitle: (t: Translator) => t('modals.title.edit-title'),
  },
  'add-new-revision-modal': {
    getComponent: () => <RevisionAddModal />,
    getTitle: (t: Translator) => t('modals.title.revision-add'),
  },
  'edit-new-revision-modal': {
    getComponent: () => <RevisionEditModal />,
    getTitle: (t: Translator) => t('modals.title.revision-edit'),
  },
  'edit-gate-route-subflow-modal': {
    getComponent: (props: { block: Gate }) => (
      <GateSelectSubflowModal {...props} />
    ),
    getTitle: (t: Translator) => t('modals.title.gate-subflow'),
  },
  'revision-set-default-modal': {
    getComponent: (props: { revisionId: string }) => (
      <RevisionSetDefaultModal {...props} />
    ),
    getTitle: (t: Translator) => t('modals.title.set-revision-as-default'),
  },
  'revision-switch': {
    getComponent: (props: { onSubmit: () => void }) => (
      <SwitchRevisionModal {...props} />
    ),
    getTitle: (t: Translator) => t('flow.blocks.changes.title'),
  },
  'more-activity-stats-modal': {
    getComponent: (props: MoreActivityStatsModalProps) => (
      <MoreActivityStatsModal {...props} />
    ),
    getTitle: (t: Translator) => t('activity-log.modal.title'),
  },
  'handover-select-inbox-modal': {
    getComponent: (props: { block: Handover }) => (
      <HandoverSelectInboxModal {...props} />
    ),
    getTitle: (t: Translator) => t('flow.form.description.handover-inbox'),
  },
  'intent-check-init-modal': {
    getComponent: (props: { block: IntentCheckBlock }) => (
      <IntentCheckInitModal {...props} />
    ),
    getTitle: (t: Translator) => t('modals.title.intent-check-init'),
  },
  'edit-data-source': {
    getComponent: () => <EditDataSource />,
    getTitle: (t: Translator) => t('data-sources.edit'),
  },
  'info-data-source': {
    getComponent: () => <InfoDataSource />,
    getTitle: (t: Translator) => t('data-sources.info'),
  },
  'info-subflow-template': {
    getComponent: () => <InfoSubflowTemplate />,
    getTitle: (t: Translator) => t('subflow-templates.info'),
  },
  'save-subflow-as-template': {
    getComponent: (props: { subflowName: string }) => (
      <SaveSubflowTemplate {...props} />
    ),
    getTitle: (t: Translator) => t('subflow-templates.create'),
  },
  'add-member': {
    getComponent: () => <AddMemberModal />,
    getTitle: (t: Translator) => t('modals.title.add-member'),
  },
  'bulk-invite-members': {
    getComponent: (props: BulkInviteDataModalProps) => (
      <BulkInviteDataModal {...props} />
    ),
    getTitle: (t: Translator) => t('modals.title.bulk-invite'),
    modalSize: 'lg' as ModalSize,
  },
  'bulk-invite-members-review': {
    getComponent: (props: TBulkInviteReviewModalProps) => (
      <BulkInviteReviewModal {...props} />
    ),
    getTitle: (t: Translator) => t('modals.title.bulk-invite-review'),
    modalSize: 'lg' as ModalSize,
  },
  'company-lifecycle-modal': {
    getComponent: (props: CompanyLifecycleModalProps) => (
      <CompanyLifecycleModal {...props} />
    ),
    getTitle: (t: Translator) => t('general-settings.lifecycle-modal.title'),
    getTitleIcon: {
      icon: 'warning',
      color: 'red-base',
    },
  },
  'conversation-review': {
    getComponent: (props: TConversationReviewModalProps) => (
      <ConversationReviewModal {...props} />
    ),
    modalSize: 'xl' as ModalSize,
    modalVariant: 'v2' as const,
    headerMargin: 'mb-1',
  },
  'intelligence-topic': {
    getComponent: (props: TIntelligenceTopicModalProps) => (
      <IntelligenceTopicModal {...props} />
    ),
    modalSize: 'lg' as ModalSize,
    modalVariant: 'v2' as const,
    titleSize: 'text-lg',
  },
  'train-ai-fact-checking': {
    getComponent: (props: TFactCheckingModalProps) => (
      <FactCheckingModal {...props} />
    ),
    getTitle: (t: Translator) => t('train-ai.fact-checking.title'),
    getTitleIcon: {
      icon: 'done_outline',
      color: 'primary-base',
    },
    modalSize: 'lg' as ModalSize,
  },
  'train-ai-fact-checking-prestart': {
    getComponent: (props: {
      selectedWeeks: number[];
      selectedConfigId: string;
      year: number;
      llmBlockId: string;
      totalCheckedItems: number;
      totalItems: number;
      updatedConversation?: (response: TCheckResult) => void;
      callback?: () => void;
    }) => <FactCheckingPrestartModal {...props} />,
    getTitle: (t: Translator) => t('train-ai.fact-checking.title'),
    getTitleIcon: {
      icon: 'done_outline',
      color: 'primary-base',
    },
  },
  'data-source-generation': {
    getComponent: (props: DataSourceGenerationModalProps) => (
      <DataSourceGenerationModal {...props} />
    ),
    getTitle: (t: Translator) =>
      t('train-ai.weekly-overview.train-ai-with-checked-data'),
    getTitleIcon: {
      icon: 'school',
      color: 'primary-base',
    },
  },
  'data-source-invalid-file-content': {
    getComponent: (props: InvalidDataSourceContent) => (
      <InvalidDataSourceContentModal {...props} />
    ),
    getTitle: (t: Translator) =>
      t('data-sources.invalid-data-source-content.title'),
    getTitleIcon: {
      icon: 'error',
      color: 'red-base',
    },
  },
  'manual-training-info': {
    getComponent: (props: {
      onConfirm: (
        automaticallyIncludeInLLM: boolean,
        selectedLLMBlocks?: LLMBlockSelectionDTO[],
      ) => void;
      sharedWithCompanyIds?: string[];
    }) => <ManualTrainingInfoModal {...props} />,
    getTitle: (t: Translator) => t('data-sources.manual-training.title'),
    getTitleIcon: {
      icon: 'lightbulb',
      color: 'primary-base',
    },
  },
  'post-process-configuration': {
    getComponent: (props: PostProcessConfigurationModalProps) => (
      <PostProcessConfigurationModal {...props} />
    ),
    getTitle: (t: Translator) =>
      t('data-sources.post-process-configuration.title'),
  },
  'weekly-statistics': {
    getComponent: (props: TWeeklyStatisticsResponse) => (
      <WeeklyStatisticsModal {...props} />
    ),
    getTitle: (t: Translator) => t('train-ai.weekly-statistics.title'),
    getTitleIcon: {
      icon: 'info',
      color: 'primary-base',
    },
  },
  'hot-topic-related-questions': {
    getComponent: (props: {
      topic: HotTopicWithRelatedDTO;
      hasBackNavigation?: boolean;
      onBack?: () => void;
    }) => <HotTopicRelatedQuestionsModal {...props} />,
    getTitle: (t: Translator) =>
      t('dashboard.trends.hot-topics.hot-topic-related-questions'),
    modalSize: 'lg' as ModalSize,
  },
  'hot-topic-all': {
    getComponent: (props: { hotTopics: HotTopicWithRelatedDTO[] }) => (
      <HotTopicAllModal {...props} />
    ),
    getTitle: (t: Translator) => t('modals.hot-topic.all.title'),
    modalSize: 'lg' as ModalSize,
  },
  'suggestion-box': {
    getComponent: () => <SuggestionBoxModal />,
    getTitle: (t: Translator) => t('suggestion-box.modal.title'),
    getTitleIcon: {
      icon: 'lightbulb',
      color: 'amber-500',
    },
    modalSize: 'lg' as ModalSize,
    modalVariant: 'v2' as const,
  },
  'experiment-result-overview': {
    getComponent: (props: { experimentResult: TExperimentResult }) => (
      <ExperimentResultModal {...props} />
    ),
    getTitle: (t: Translator) => t('experiment-result.modal.title'),
    getTitleIcon: {
      icon: 'science',
      color: 'primary-base',
    },
    modalSize: 'w-90' as ModalSize,
  },
  'test-result-overview': {
    getComponent: (props: { testResult: TTestAccuracyResult }) => (
      <TestResultsReviewDataModal {...props} />
    ),
    getTitle: (t: Translator) => t('test-results.overview.title'),
    getTitleIcon: {
      icon: 'visibility',
      color: 'primary-base',
    },
    modalSize: 'w-90' as ModalSize,
  },
  'add-template-response': {
    getComponent: () => <CreateTemplateResponseModal />,
    getTitle: (t: Translator) => t('modals.title.add-template-response'),
  },
  'edit-template-response': {
    getComponent: () => <EditTemplateResponseModal />,
    getTitle: (t: Translator) => t('modals.title.edit-template-response'),
  },
  'create-keyword': {
    getComponent: () => <CreateKeywordModal />,
    getTitle: (t: Translator) => t('modals.title.create-keyword'),
  },
  'edit-keyword': {
    getComponent: () => <EditKeywordModal />,
    getTitle: (t: Translator) => t('modals.title.edit-keyword'),
  },
  'share-data-source': {
    getComponent: () => <ShareDataSource />,
    getTitle: (t: Translator) => t('modals.title.share-data-source'),
    getTitleIcon: {
      icon: 'share',
      color: 'primary-base',
    },
  },
  'move-data-source': {
    getComponent: () => <MoveDataSource />,
    getTitle: (t: Translator) => t('modals.title.move-data-source'),
    getTitleIcon: {
      icon: 'drive_file_move',
      color: 'primary-base',
    },
  },
  'share-member': {
    getComponent: (props: { member: CompanyMemberDTO }) => (
      <ShareMemberModal {...props} />
    ),
    getTitle: (t: Translator) => t('modals.title.share-member'),
    getTitleIcon: {
      icon: 'share',
      color: 'primary-base',
    },
  },
  'web-scraper-progress': {
    getComponent: (props: WebScraperProgressModalProps) => (
      // Use jobId as the React key so that each new scraping job (including retries)
      // mounts a fresh component instance, preventing stale state from leaking across attempts.
      <WebScraperProgressModal key={props.jobId ?? 'pending'} {...props} />
    ),
    getTitle: (t: Translator) => t('modals.title.web-scraper-progress'),
    getTitleIcon: {
      icon: 'cloud_download',
      color: 'primary-base',
    },
    modalSize: 'md' as ModalSize,
  },
  'upload-documents-progress': {
    getComponent: (props: UploadDocumentsProgressModalProps) => (
      <UploadDocumentsProgressModal {...props} />
    ),
    getTitle: (t: Translator) => t('modals.title.upload-documents-progress'),
    modalSize: 'md' as ModalSize,
  },
  'conversation-info': {
    getComponent: (props: TConversationDetailsSchema) => (
      <ConversationInfoModal {...props} />
    ),
    getTitle: (t: Translator) => t('train-ai.conversation-info.title'),
    getTitleIcon: {
      icon: 'info',
      color: 'primary-base',
    },
    modalSize: 'lg' as ModalSize,
  },
  'create-subfolder': {
    getComponent: (props: CreateSubfolderModalProps) => (
      <CreateSubfolderModal {...props} />
    ),
    getTitle: (t: Translator) =>
      t('data-sources.modals.create-subfolder.title'),
  },
  'conversation-keywords': {
    getComponent: (props: {
      conversation: TConversationDetailsSchema;
      onSave: (keywords: string[]) => void;
    }) => <ConversationKeywordsModal {...props} />,
    getTitle: (t: Translator) => t('train-ai.keywords.modal-title'),
    getTitleIcon: {
      icon: 'tag',
      color: 'primary-base',
    },
  },
  'knowledge-article-read': {
    getComponent: (props: KnowledgeArticleDTO) => (
      <ArticleReadModal {...props} />
    ),
    modalSize: 'lg' as ModalSize,
  },
};

export type ModalType = keyof typeof modalMap;

export const modals: Record<ModalType, TModal> = modalMap;

export interface GeneralPromptModalProps {
  description: string;
  onSubmit: () => void;
  onAbort?: () => void;
  type?: 'delete' | 'info' | 'confirm' | 'custom';
  shouldAutomaticallyClose?: boolean;
  customButtons?: Array<{
    text: string;
    onClick: () => void;
    styling?: 'primary' | 'secondary' | 'danger' | 'cancel';
  }>;
}

export interface GeneralInfoModalProps {
  text: string;
  options?: {
    shouldCloseButton?: boolean;
  };
}
